import { CheckCircle2, ShieldCheck, FileText, ArrowRight, Star } from 'lucide-react'
import { Link } from 'react-router-dom'
import { appRoutes } from '@/constants'
import { useHomepageReviews } from '@/modules/reviews/hooks/use-reviews'

// Fallback verified automotive customer reviews
const DEFAULT_VERIFIED_REVIEWS = [
  {
    id: 101,
    name: 'Aarav Mehta',
    handle: 'Verified Part Buyer',
    avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80',
    itemTitle: 'BMW 3 Series Brembo Brake Kit',
    reviewType: 'PART',
    rating: 5,
    text: 'Received OEM Brembo brake pads for my 330i. Packaging was intact, fitment was spot on, and braking responsiveness is flawless.',
  },
  {
    id: 102,
    name: 'Vikram Singhania',
    handle: 'Verified Car Deal',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&auto=format&fit=crop&q=80',
    itemTitle: 'Mercedes C200 Dealership Deal',
    reviewType: 'CAR_DEAL',
    rating: 5,
    text: 'Transacted through verified dealer AutoCraft. Seamless negotiation, verified 150-point inspection check, and instant RC transfer guidance.',
  },
  {
    id: 103,
    name: 'Rohan Deshmukh',
    handle: 'Verified Part Buyer',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&auto=format&fit=crop&q=80',
    itemTitle: 'Audi A4 Suspension Strut Assembly',
    reviewType: 'PART',
    rating: 5,
    text: 'Delivery arrived 2 days earlier than expected. Part number matched factory specifications exactly. Installed with zero hassles.',
  },
  {
    id: 104,
    name: 'Pooja Kashyap',
    handle: 'Verified Car Deal',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&auto=format&fit=crop&q=80',
    itemTitle: 'Hyundai Tucson 4WD Dealership Deal',
    reviewType: 'CAR_DEAL',
    rating: 5,
    text: 'The dealership was remarkably transparent regarding previous service logs and tyre depth. Delighted with the overall car purchase experience.',
  },
  {
    id: 105,
    name: 'Devansh Verma',
    handle: 'Verified Part Buyer',
    avatar: 'https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?w=100&auto=format&fit=crop&q=80',
    itemTitle: 'Toyota Fortuner OEM Air Intake Kit',
    reviewType: 'PART',
    rating: 5,
    text: 'Genuine OEM high-flow filter. Engine airflow noticeably improved. Verified seller delivery packaging with warranty seals.',
  },
  {
    id: 106,
    name: 'Siddharth Nair',
    handle: 'Verified Car Deal',
    avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=100&auto=format&fit=crop&q=80',
    itemTitle: 'Skoda Octavia vRS Dealership Deal',
    reviewType: 'CAR_DEAL',
    rating: 5,
    text: 'Dealer kept their word on test drive schedules and provided full vehicle scan diagnostics prior to handover. Superb dealership service.',
  },
]

// 3. Trust Badges
const TRUST_BADGES = [
  {
    icon: CheckCircle2,
    title: '150-POINT AUDITED',
    desc: 'Every car undergoes a rigorous multi-point physical & engine inspection.',
  },
  {
    icon: FileText,
    title: 'FREE HISTORY LOGS',
    desc: 'Access verified service logs, odometer readings & ownership titles instantly.',
  },
  {
    icon: ShieldCheck,
    title: 'ASSURED RC TRANSFERS',
    desc: 'Hassle-free registration transfers, legal compliance & dedicated support.',
  },
]


export function FigmaBottomSections() {
  const { data: dbReviews = [] } = useHomepageReviews()

  // Format database reviews to match layout
  const liveReviews = dbReviews.map((r: any) => ({
    id: r.id,
    name: r.userName || 'Verified Customer',
    handle: r.reviewType === 'PART' ? 'Verified Part Buyer' : 'Verified Car Deal',
    avatar: r.userAvatar || null,
    itemTitle: r.itemTitle || (r.reviewType === 'PART' ? 'OEM Auto Spare Part' : 'Vehicle Dealership Service'),
    reviewType: r.reviewType,
    rating: r.rating || 5,
    text: r.comment,
  }))

  // Fill up to 6 with realistic fallback items
  const combined = [...liveReviews]
  for (const fallback of DEFAULT_VERIFIED_REVIEWS) {
    if (combined.length >= 6) break
    if (!combined.some((c) => c.text === fallback.text)) {
      combined.push(fallback)
    }
  }

  const row1 = combined.slice(0, 3)
  const row2 = combined.slice(3, 6)

  return (
    <div className="space-y-16 py-12 relative">
      {/* ========================================================================= */}
      {/* 1. TESTIMONIALS SECTION (2 ROWS OF CARDS) */}
      {/* ========================================================================= */}
      <section className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 space-y-6">
        {/* Row 1 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {row1.map((item) => (
            <div
              key={item.id}
              className="p-5 rounded-2xl border border-border/60 bg-card/60 backdrop-blur-md space-y-3 shadow-xs hover:border-primary/40 transition-colors"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  {item.avatar ? (
                    <img src={item.avatar} alt={item.name} className="h-10 w-10 rounded-full object-cover border border-border shrink-0" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center font-bold text-primary text-xs shrink-0">
                      {item.name?.[0] || 'U'}
                    </div>
                  )}
                  <div className="min-w-0">
                    <h4 className="text-sm font-bold text-foreground leading-snug truncate">{item.name}</h4>
                    <span className="text-[10px] font-medium text-emerald-500 flex items-center gap-1">
                      <CheckCircle2 className="size-3 shrink-0" />
                      {item.handle}
                    </span>
                  </div>
                </div>

                {/* Rating Stars */}
                <div className="flex items-center gap-0.5 shrink-0">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      className={`size-3.5 ${
                        (item.rating || 5) >= s
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-muted-foreground/30'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {item.itemTitle && (
                <div className="text-[11px] font-semibold text-primary truncate">
                  {item.itemTitle}
                </div>
              )}

              <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">
                "{item.text}"
              </p>
            </div>
          ))}
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {row2.map((item) => (
            <div
              key={item.id}
              className="p-5 rounded-2xl border border-border/60 bg-card/60 backdrop-blur-md space-y-3 shadow-xs hover:border-primary/40 transition-colors"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  {item.avatar ? (
                    <img src={item.avatar} alt={item.name} className="h-10 w-10 rounded-full object-cover border border-border shrink-0" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center font-bold text-primary text-xs shrink-0">
                      {item.name?.[0] || 'U'}
                    </div>
                  )}
                  <div className="min-w-0">
                    <h4 className="text-sm font-bold text-foreground leading-snug truncate">{item.name}</h4>
                    <span className="text-[10px] font-medium text-emerald-500 flex items-center gap-1">
                      <CheckCircle2 className="size-3 shrink-0" />
                      {item.handle}
                    </span>
                  </div>
                </div>

                {/* Rating Stars */}
                <div className="flex items-center gap-0.5 shrink-0">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      className={`size-3.5 ${
                        (item.rating || 5) >= s
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-muted-foreground/30'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {item.itemTitle && (
                <div className="text-[11px] font-semibold text-primary truncate">
                  {item.itemTitle}
                </div>
              )}

              <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">
                "{item.text}"
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 2. DUAL FEATURED DARK CARDS (PRE-OWNED & GENUINE AUTO SPARES) */}
      {/* ========================================================================= */}
      <section className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Card 1: Pre-Owned Vehicles */}
          <div className="relative overflow-hidden rounded-3xl bg-slate-950 p-8 text-white min-h-[320px] flex flex-col justify-between shadow-2xl border border-white/10">
            <div
              className="absolute inset-0 bg-cover bg-center opacity-40"
              style={{ backgroundImage: `url('https://images.unsplash.com/photo-1614162692292-7ac56d7f7f1e?q=80&w=800&auto=format&fit=crop')` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent" />

            <div className="relative z-10 space-y-4">
              <h3 className="font-display text-2xl font-extrabold tracking-tight uppercase">
                PRE-OWNED &amp; RESOLD VEHICLES
              </h3>
              <p className="text-xs text-gray-300 max-w-sm">
                Find the perfect companion from our certified range of inspected pre-owned cars. Every vehicle undergoes a multi-point inspection process.
              </p>

              <div className="space-y-2 pt-2">
                <div className="flex items-center gap-2 text-xs text-gray-200">
                  <CheckCircle2 className="h-4 w-4 text-[#CF2F2F]" />
                  <span>150-Point Technical Check</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-200">
                  <CheckCircle2 className="h-4 w-4 text-[#CF2F2F]" />
                  <span>Verified Ownership &amp; Clean History</span>
                </div>
              </div>
            </div>

            <div className="relative z-10 pt-6">
              <Link
                to={appRoutes.cars}
                style={{ backgroundColor: '#CF2F2F' }}
                className="inline-flex items-center gap-2 rounded-xl px-5 py-3 text-xs font-bold text-white uppercase tracking-wider shadow-md hover:opacity-90 transition-all"
              >
                <span>EXPLORE PRE-OWNED</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* Card 2: Genuine Auto Spares */}
          <div className="relative overflow-hidden rounded-3xl bg-slate-950 p-8 text-white min-h-[320px] flex flex-col justify-between shadow-2xl border border-white/10">
            <div
              className="absolute inset-0 bg-cover bg-center opacity-40"
              style={{ backgroundImage: `url('https://images.unsplash.com/photo-1486006920555-c77dce18193b?q=80&w=800&auto=format&fit=crop')` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent" />

            <div className="relative z-10 space-y-4">
              <h3 className="font-display text-2xl font-extrabold tracking-tight uppercase">
                GENUINE AUTO SPARES
              </h3>

              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-md space-y-1">
                  <span className="text-[10px] font-bold text-[#CF2F2F] uppercase block">NEW OEM PARTS</span>
                  <h5 className="text-xs font-bold">Original OEM Spares</h5>
                  <p className="text-[10px] text-gray-400">Factory-fresh parts with warranty check.</p>
                  <Link to={appRoutes.parts} className="text-[10px] font-bold text-[#CF2F2F] hover:underline block pt-1">
                    Shop New Spares &rarr;
                  </Link>
                </div>

                <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-md space-y-1">
                  <span className="text-[10px] font-bold text-emerald-400 uppercase block">USED / RECYCLED PARTS</span>
                  <h5 className="text-xs font-bold">Tested Used Spares</h5>
                  <p className="text-[10px] text-gray-400">Affordable, tested original spares.</p>
                  <Link to={appRoutes.parts} className="text-[10px] font-bold text-emerald-400 hover:underline block pt-1">
                    Shop Used Spares &rarr;
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 3. TRUST BADGES ROW (150-POINT, HISTORY LOGS, RC TRANSFERS) */}
      {/* ========================================================================= */}
      <section className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TRUST_BADGES.map((item, idx) => {
            const Icon = item.icon
            return (
              <div
                key={idx}
                className="p-6 rounded-2xl border border-border bg-card shadow-xs flex items-start gap-4"
              >
                <div className="h-10 w-10 rounded-full bg-red-500/10 text-[#CF2F2F] flex items-center justify-center shrink-0">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-xs font-extrabold text-foreground tracking-wider uppercase">{item.title}</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              </div>
            )
          })}
        </div>
      </section>
    </div>
  )
}
